import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import "./style.css";

export default function PrivacyPolicy() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const canonicalUrl = `https://bur-han.ca${location.pathname}`;

    let linkTag = document.querySelector('link[rel="canonical"]');

    if (linkTag) {
      linkTag.setAttribute("href", canonicalUrl);
    } else {
      linkTag = document.createElement("link");
      linkTag.setAttribute("rel", "canonical");
      linkTag.setAttribute("href", canonicalUrl);
      document.head.appendChild(linkTag);
    }

    document.title = "Privacy Policy | BUR-HAN Garden & Lawn Care";

    const desc =
      "Privacy Policy for BUR-HAN Garden & Lawn Care. Learn how we collect, use, disclose, retain, and protect personal information in accordance with British Columbia privacy law.";

    let metaDesc = document.querySelector('meta[name="description"]');

    if (metaDesc) {
      metaDesc.setAttribute("content", desc);
    } else {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      metaDesc.setAttribute("content", desc);
      document.head.appendChild(metaDesc);
    }

    let ogTitle = document.querySelector('meta[property="og:title"]');

    if (ogTitle) {
      ogTitle.setAttribute("content", document.title);
    } else {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      ogTitle.setAttribute("content", document.title);
      document.head.appendChild(ogTitle);
    }

    let ogDesc = document.querySelector(
      'meta[property="og:description"]'
    );

    if (ogDesc) {
      ogDesc.setAttribute("content", desc);
    } else {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      ogDesc.setAttribute("content", desc);
      document.head.appendChild(ogDesc);
    }
  }, [location.pathname]);

  return (
    <div className="wrapper">
      <Header />

      <div className="serviceItemsHeader">
        <h1 data-aos="zoom-in">Privacy Policy</h1>
      </div>

      <div className="privacyPolicyMain">
        <p>
          <strong>Last Updated: August 28, 2026</strong>
        </p>

        <p>
          BUR-HAN Garden &amp; Lawn Care ("BUR-HAN", "we", "us" or
          "our") respects your privacy and is committed to protecting
          the Personal Information entrusted to us.
        </p>

        <p>
          This Privacy Policy ("Policy") describes how BUR-HAN collects,
          uses, discloses, retains, and protects Personal Information
          obtained through the bur-han.ca website ("Website"), our
          online forms, communications, and related landscaping and lawn
          care services (collectively, the "Services").
        </p>

        <p>
          This Policy also explains your privacy rights and the choices
          available to you regarding your Personal Information.
        </p>

        <h2>Privacy Legislation</h2>

        <p>
          BUR-HAN handles Personal Information in accordance with
          British Columbia's Personal Information Protection Act
          ("PIPA") and other applicable Canadian privacy legislation.
          Where applicable, federal privacy requirements may also apply
          to certain activities involving Personal Information.
        </p>

        <p>
          Electronic marketing communications are also subject to
          Canada's Anti-Spam Legislation ("CASL"), where applicable.
        </p>

        <h2>Accountability And Privacy Officer</h2>

        <p>
          BUR-HAN is responsible for Personal Information under its
          control. We have designated a Privacy Officer who is
          responsible for overseeing our privacy practices and our
          compliance with applicable privacy legislation.
        </p>

        <p>
          Questions, concerns, complaints, access requests, correction
          requests, or requests relating to your Personal Information
          may be directed to our Privacy Officer using the contact
          information provided at the end of this Policy.
        </p>

        <h2>Collection Of Personal Information</h2>

        <p>
          You may browse most areas of our Website without directly
          identifying yourself. However, when you request a quote,
          contact us, submit a form, inquire about our Services, or
          otherwise communicate with BUR-HAN, we may collect Personal
          Information that you voluntarily provide.
        </p>

        <p>Depending on your interaction with us, this may include:</p>

        <ul>
          <li>Your name</li>
          <li>Email address</li>
          <li>Telephone number</li>
          <li>Property or service address</li>
          <li>City, municipality, or service area</li>
          <li>Information relating to a requested landscaping service</li>
          <li>Property or project details</li>
          <li>Photographs or other materials you voluntarily provide</li>
          <li>Messages, comments, questions, or feedback</li>
          <li>
            Information necessary to prepare an estimate, schedule a
            service, or respond to an inquiry
          </li>
          <li>
            Any other Personal Information that you voluntarily provide
            to us
          </li>
        </ul>

        <p>
          We seek to collect only the Personal Information that is
          reasonably necessary for the purposes identified in this
          Policy or communicated to you at the time of collection.
        </p>

        <h2>Automatic Collection Of Information</h2>

        <p>
          When you access the Website, certain information may be
          collected automatically by our web server, hosting providers,
          analytics tools, or similar technologies.
        </p>

        <p>This information may include:</p>

        <ul>
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Device type</li>
          <li>Operating system</li>
          <li>Language preferences</li>
          <li>Referring webpage or source</li>
          <li>Pages visited on our Website</li>
          <li>Date and time of visits</li>
          <li>Approximate geographic information</li>
          <li>Website interaction and traffic information</li>
        </ul>

        <p>
          This information may be used for security, fraud prevention,
          analytics, troubleshooting, Website administration, and
          improving the performance and usability of our Website.
        </p>

        <h2>Consent</h2>

        <p>
          Where required by law, BUR-HAN obtains consent before
          collecting, using, or disclosing Personal Information.
        </p>

        <p>
          Consent may be express or implied depending on the
          circumstances, the sensitivity of the information, and your
          reasonable expectations.
        </p>

        <p>
          For example, when you submit your name, contact information,
          property information, and project details through our quote or
          contact forms, you consent to BUR-HAN using that information
          to respond to your request and provide information regarding
          our Services.
        </p>

        <p>
          In certain circumstances, Personal Information may be
          collected, used, or disclosed without consent where permitted
          or required by applicable law.
        </p>

        <h2>Withdrawal Of Consent</h2>

        <p>
          Where BUR-HAN relies on your consent, you may withdraw that
          consent at any time, subject to legal or contractual
          restrictions and reasonable notice.
        </p>

        <p>
          Withdrawal of consent may affect our ability to provide
          certain Services. For example, if you withdraw consent for us
          to use your contact information, we may be unable to respond
          to a quote request or communicate with you regarding a
          scheduled service.
        </p>

        <h2>Use Of Personal Information</h2>

        <p>BUR-HAN may use Personal Information to:</p>

        <ul>
          <li>Respond to inquiries and quote requests</li>
          <li>Prepare landscaping or lawn care estimates</li>
          <li>Schedule and coordinate Services</li>
          <li>Provide requested landscaping and lawn care Services</li>
          <li>Communicate regarding appointments or scheduled work</li>
          <li>Provide customer service and support</li>
          <li>Send administrative or transactional communications</li>
          <li>Request customer feedback</li>
          <li>Maintain customer and service records</li>
          <li>Improve our Website and Services</li>
          <li>Analyze Website traffic and performance</li>
          <li>Maintain the security of our Website and systems</li>
          <li>Detect or prevent fraudulent or unauthorized activity</li>
          <li>Meet legal, regulatory, accounting, or reporting obligations</li>
          <li>
            Carry out other purposes that we identify when the
            information is collected
          </li>
        </ul>

        <h2>Limiting Collection</h2>

        <p>
          BUR-HAN limits the collection of Personal Information to
          information that is reasonably necessary for the purposes
          identified by us.
        </p>

        <p>
          We do not intend to collect Personal Information that is
          unrelated to our legitimate business activities or the
          provision of our Services.
        </p>

        <h2>Disclosure Of Personal Information</h2>

        <p>
          BUR-HAN does not sell your Personal Information.
        </p>

        <p>
          We may disclose Personal Information to contractors, service
          providers, technology providers, and other organizations that
          assist us with business operations or the delivery of our
          Services.
        </p>

        <p>These providers may assist with services such as:</p>

        <ul>
          <li>Website hosting</li>
          <li>Email communications</li>
          <li>Customer communications</li>
          <li>Scheduling</li>
          <li>Invoicing or payment administration</li>
          <li>Website analytics</li>
          <li>IT support and security</li>
          <li>Other operational services</li>
        </ul>

        <p>
          We seek to provide service providers only with the
          information reasonably necessary for them to perform their
          services.
        </p>

        <p>
          Personal Information may also be disclosed where permitted or
          required by law, including where disclosure is necessary to:
        </p>

        <ul>
          <li>Respond to a lawful court order, subpoena, or legal process</li>
          <li>Comply with applicable laws or regulations</li>
          <li>Investigate suspected fraud or unlawful activity</li>
          <li>Protect BUR-HAN's legal rights or property</li>
          <li>
            Protect the safety of our customers, employees, contractors,
            or members of the public
          </li>
        </ul>

        <h2>Service Providers Outside British Columbia Or Canada</h2>

        <p>
          Some service providers used by BUR-HAN may store or process
          information outside British Columbia or outside Canada.
        </p>

        <p>
          When Personal Information is processed in another
          jurisdiction, it may be subject to the laws of that
          jurisdiction and may be accessible to courts, law enforcement,
          or governmental authorities in accordance with applicable
          laws.
        </p>

        <h2>Retention Of Personal Information</h2>

        <p>
          BUR-HAN retains Personal Information only for as long as
          reasonably necessary to fulfil the purposes for which it was
          collected or as required for legitimate business, legal,
          accounting, contractual, or regulatory purposes.
        </p>

        <p>
          When Personal Information is no longer reasonably required,
          we will securely destroy, delete, or anonymize it, subject to
          applicable legal requirements and our record-retention
          obligations.
        </p>

        <h2>Accuracy Of Personal Information</h2>

        <p>
          We take reasonable steps to ensure that Personal Information
          used to make a decision that directly affects an individual
          is as accurate and complete as reasonably necessary for the
          purposes for which it is used.
        </p>

        <p>
          You may contact us if you believe that Personal Information
          we maintain about you is incorrect or incomplete.
        </p>

        <h2>Access To And Correction Of Personal Information</h2>

        <p>
          Subject to applicable legal exceptions, you may request
          access to Personal Information that BUR-HAN holds about you.
        </p>

        <p>
          You may also request that we correct Personal Information
          where you believe it is inaccurate or incomplete.
        </p>

        <p>
          We may need to verify your identity before responding to an
          access or correction request.
        </p>

        <p>
          In certain circumstances, applicable law may permit or require
          us to refuse access to some information. If we refuse all or
          part of a request, we will provide an explanation where
          required by law.
        </p>

        <p>
          Requests may be submitted to our Privacy Officer using the
          contact information listed below.
        </p>

        <h2>Cookies And Similar Technologies</h2>

        <p>
          Our Website may use cookies and similar technologies to help
          operate the Website, maintain security, remember preferences,
          understand Website usage, and improve Website performance.
        </p>

        <p>
          A cookie is a small text file placed on your device by a
          website. Cookies cannot execute programs or deliver viruses to
          your device.
        </p>

        <p>
          Depending on your browser settings, you may be able to reject
          or delete cookies. Disabling certain cookies may affect some
          Website functionality.
        </p>

        <h2>Data Analytics</h2>

        <p>
          Our Website may use third-party analytics and website
          measurement tools, including Google Analytics and related
          Google technologies, to understand Website usage and
          performance.
        </p>

        <p>
          These technologies may collect information such as pages
          viewed, approximate location, browser and device information,
          traffic sources, interactions with Website content, and the
          duration or frequency of Website visits.
        </p>

        <p>
          We use this information to understand how visitors interact
          with our Website, improve Website functionality, and evaluate
          Website performance.
        </p>

        <h2>Advertising And Measurement Technologies</h2>

        <p>
          BUR-HAN may use advertising, conversion measurement, or
          similar technologies to understand whether our advertising
          campaigns result in Website visits, inquiries, quote requests,
          or other interactions.
        </p>

        <p>
          These technologies may use cookies or similar identifiers.
          The information collected and processed by third-party
          providers is also subject to those providers' privacy
          practices.
        </p>

        <h2>Do Not Track Signals</h2>

        <p>
          Some browsers include a "Do Not Track" setting that may send
          a signal indicating that a user does not wish to have certain
          online activity tracked.
        </p>

        <p>
          Because there is currently no universally accepted standard
          governing how websites must respond to these signals, our
          Website may not respond to all Do Not Track signals.
        </p>

        <h2>Email Marketing And CASL</h2>

        <p>
          BUR-HAN may send promotional emails, seasonal service
          information, newsletters, or similar electronic marketing
          communications where permitted by law.
        </p>

        <p>
          Where consent is required, we will seek the appropriate
          consent before sending commercial electronic messages.
        </p>

        <p>
          Marketing communications sent by BUR-HAN will include
          information identifying BUR-HAN and an unsubscribe mechanism
          where required by Canada's Anti-Spam Legislation ("CASL").
        </p>

        <p>
          You may unsubscribe from marketing communications at any time
          by using the unsubscribe mechanism included in the message or
          by contacting us.
        </p>

        <p>
          Unsubscribing from marketing communications does not
          necessarily prevent BUR-HAN from sending administrative,
          transactional, appointment, quote, or service-related
          communications that are necessary to provide requested
          Services.
        </p>

        <h2>Privacy Of Children</h2>

        <p>
          Our Website and Services are not directed toward children, and
          we do not knowingly collect Personal Information from children
          through the Website.
        </p>

        <p>
          If you believe that a child has provided Personal Information
          to BUR-HAN without appropriate authorization, please contact
          our Privacy Officer so that we can review the matter and take
          appropriate action.
        </p>

        <h2>Information Security</h2>

        <p>
          BUR-HAN uses reasonable administrative, technical, and
          physical safeguards designed to protect Personal Information
          under our control against unauthorized access, collection,
          use, disclosure, copying, modification, disposal, or similar
          risks.
        </p>

        <p>
          Our security measures may include access controls, technical
          protections, secure systems, staff practices, and other
          safeguards appropriate to the sensitivity of the information.
        </p>

        <p>
          However, no method of transmission or electronic storage can
          be guaranteed to be completely secure. Accordingly, we cannot
          guarantee absolute security of information transmitted through
          the Internet.
        </p>

        <h2>Privacy And Security Incidents</h2>

        <p>
          If BUR-HAN becomes aware of a privacy or security incident
          involving Personal Information, we will assess the incident
          and take reasonable steps to contain, investigate, and address
          it.
        </p>

        <p>
          Where notification or reporting is required by applicable
          law, we will take appropriate steps to notify affected
          individuals, regulators, authorities, or other parties as
          required.
        </p>

        <h2>Links To Other Websites</h2>

        <p>
          Our Website may contain links to websites or online services
          operated by third parties.
        </p>

        <p>
          BUR-HAN is not responsible for the privacy practices, content,
          or security of third-party websites. We encourage you to
          review the privacy policies of any third-party website before
          providing Personal Information.
        </p>

        <h2>Complaints And Privacy Concerns</h2>

        <p>
          If you have a question or concern about how BUR-HAN handles
          your Personal Information, we encourage you to contact our
          Privacy Officer.
        </p>

        <p>
          We will review privacy complaints and make reasonable efforts
          to investigate and address concerns in accordance with
          applicable privacy legislation.
        </p>

        <p>
          Individuals may also have the right to contact the Office of
          the Information and Privacy Commissioner for British Columbia
          regarding privacy matters governed by British Columbia
          privacy legislation.
        </p>

        <h2>Changes To This Privacy Policy</h2>

        <p>
          BUR-HAN may update this Privacy Policy from time to time to
          reflect changes in our practices, Services, technologies, or
          legal obligations.
        </p>

        <p>
          When this Policy is updated, we will revise the "Last Updated"
          date displayed on this page.
        </p>

        <p>
          We encourage you to periodically review this page for changes.
        </p>

        <h2>Contacting Our Privacy Officer</h2>

        <p>
          If you have questions, concerns, complaints, or requests
          regarding this Privacy Policy or your Personal Information,
          please contact:
        </p>

        <ul className="privacyContact">
          <li>
            <strong>Privacy Officer</strong>
          </li>

          <li>
            <strong>BUR-HAN Garden &amp; Lawn Care</strong>
          </li>

          <li>
            Mailing Address: Suite #314, 1641 Lonsdale Ave, North
            Vancouver, BC V7M 2J5
          </li>

          <li>
            Shop Address: A-2323 Old Dollarton Rd, North Vancouver, BC
            V7H 1A9
          </li>

          <li>
            Phone:{" "}
            <a href="tel:604-983-2687">
              (604) 983-2687
            </a>
          </li>

          <li>
            Email:{" "}
            <a href="mailto:gardenandlawncare@bur-han.ca">
              gardenandlawncare@bur-han.ca
            </a>
          </li>

          <li>
            Contact form:{" "}
            <a href="/contact-us">
              bur-han.ca/contact-us
            </a>
          </li>
        </ul>

        <p>
          BUR-HAN will make reasonable efforts to respond to privacy
          inquiries, complaints, and requests within the timeframes
          required by applicable privacy legislation.
        </p>
      </div>

      <Footer />
    </div>
  );
}